import Swiper from "swiper";
import { Navigation, Pagination, Autoplay, EffectFade, EffectCube, EffectFlip, EffectCoverflow, EffectCards, EffectCreative } from "swiper/modules";
import { sanitizeCssValue as sc } from "@app/widgets/shared/btnHoverCss";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import "swiper/css/effect-cube";
import "swiper/css/effect-flip";
import "swiper/css/effect-coverflow";
import "swiper/css/effect-cards";
import "swiper/css/effect-creative";

export type SliderWidgetProps = {
  slidesPerView: number;
  spaceBetween: number;
  autoplay: boolean;
  autoplayDelay: number;
  loop: boolean;
  showNavigation: boolean;
  showPagination: boolean;
  paginationType: string;
  effect: string;
  speed: number;
  navColor: string;
  navSize: string;
  paginationColor: string;
  paginationActiveColor: string;
  slideMinHeight: string;
};

export const parseSliderProps = (container: HTMLElement): SliderWidgetProps => {
  const attr = (name: string, fallback = "") => container.getAttribute(name) || fallback;
  return {
    slidesPerView: parseInt(attr("data-slides-per-view", "1")) || 1,
    spaceBetween: parseInt(attr("data-space-between", "20")) || 20,
    autoplay: attr("data-autoplay") === "true",
    autoplayDelay: parseInt(attr("data-autoplay-delay", "3000")) || 3000,
    loop: attr("data-loop") === "true",
    showNavigation: attr("data-show-navigation", "true") === "true",
    showPagination: attr("data-show-pagination", "true") === "true",
    paginationType: attr("data-pagination-type", "bullets"),
    effect: attr("data-effect", "slide"),
    speed: parseInt(attr("data-speed", "300")) || 300,
    navColor: attr("data-nav-color", "#333333"),
    navSize: attr("data-nav-size", "24px"),
    paginationColor: attr("data-pagination-color", "#cccccc"),
    paginationActiveColor: attr("data-pagination-active-color", "#1890ff"),
    slideMinHeight: attr("data-slide-min-height", "200px"),
  };
};

export const initSlider = (container: HTMLElement, props: SliderWidgetProps) => {
  if (container.dataset.ezInit === "slider") return;

  const slideEls = Array.from(container.querySelectorAll<HTMLElement>(":scope > .ez-slide, :scope > div > .ez-slide"));
  if (slideEls.length === 0) return;

  container.dataset.ezInit = "slider";

  const swiperContainer = document.createElement("div");
  swiperContainer.className = "swiper";
  swiperContainer.style.overflow = "hidden";

  const swiperWrapper = document.createElement("div");
  swiperWrapper.className = "swiper-wrapper";

  slideEls.forEach((slide) => {
    const swiperSlide = document.createElement("div");
    swiperSlide.className = "swiper-slide";
    swiperSlide.style.overflow = "hidden";
    swiperSlide.style.boxSizing = "border-box";
    swiperSlide.appendChild(slide);
    swiperWrapper.appendChild(swiperSlide);
  });

  swiperContainer.appendChild(swiperWrapper);

  let navNext: HTMLElement | undefined;
  let navPrev: HTMLElement | undefined;
  if (props.showNavigation) {
    navNext = document.createElement("div");
    navNext.className = "swiper-button-next";
    navPrev = document.createElement("div");
    navPrev.className = "swiper-button-prev";
    swiperContainer.appendChild(navNext);
    swiperContainer.appendChild(navPrev);
  }

  let paginationEl: HTMLElement | undefined;
  if (props.showPagination) {
    paginationEl = document.createElement("div");
    paginationEl.className = "swiper-pagination";
    swiperContainer.appendChild(paginationEl);
  }

  container.innerHTML = "";
  container.style.overflow = "hidden";
  container.appendChild(swiperContainer);

  const styleEl = document.createElement("style");
  const navColor = sc(props.navColor || "#333");
  const navSize = sc(props.navSize || "24px");
  const paginationColor = sc(props.paginationColor || "#ccc");
  const paginationActiveColor = sc(props.paginationActiveColor || "#1890ff");
  styleEl.textContent = `
    .swiper-button-next, .swiper-button-prev {
      color: ${navColor} !important;
      --swiper-navigation-size: ${navSize};
    }
    .swiper-pagination-bullet {
      background: ${paginationColor} !important;
      opacity: 1 !important;
    }
    .swiper-pagination-bullet-active {
      background: ${paginationActiveColor} !important;
    }
    .swiper-pagination-progressbar .swiper-pagination-progressbar-fill {
      background: ${paginationActiveColor} !important;
    }
    .swiper-pagination-fraction {
      color: ${paginationActiveColor} !important;
    }
    .swiper-wrapper {
      align-items: stretch !important;
    }
    .swiper-slide {
      height: auto !important;
      min-height: ${sc(props.slideMinHeight || "200px")};
      overflow: hidden;
    }
    .swiper-slide > * {
      height: 100%;
    }
  `;
  container.appendChild(styleEl);

  const modules: any[] = [];
  if (props.showNavigation) modules.push(Navigation);
  if (props.showPagination) modules.push(Pagination);
  if (props.autoplay) modules.push(Autoplay);

  const effectMap: Record<string, any> = {
    fade: EffectFade,
    cube: EffectCube,
    flip: EffectFlip,
    coverflow: EffectCoverflow,
    cards: EffectCards,
    "creative-parallax": EffectCreative,
    "creative-rotate": EffectCreative,
  };
  if (effectMap[props.effect]) modules.push(effectMap[props.effect]);

  const isMultiSlide = props.effect === "slide" || props.effect === "coverflow";
  const isCreative = props.effect.startsWith("creative-");
  const swiperEffect = isCreative ? "creative" : props.effect;

  const config: any = {
    modules,
    slidesPerView: isMultiSlide ? props.slidesPerView : 1,
    spaceBetween: isMultiSlide ? props.spaceBetween : 0,
    speed: props.speed,
    effect: swiperEffect,
    loop: props.loop,
    grabCursor: true,
  };

  if (isMultiSlide && props.slidesPerView > 1) {
    config.breakpoints = {
      0: { slidesPerView: 1 },
      480: { slidesPerView: Math.min(2, props.slidesPerView) },
      768: { slidesPerView: Math.min(3, props.slidesPerView) },
      1024: { slidesPerView: props.slidesPerView },
    };
  }

  if (props.effect === "creative-parallax") {
    config.creativeEffect = {
      prev: { shadow: true, translate: ["-20%", 0, -1] },
      next: { translate: ["100%", 0, 0] },
    };
  } else if (props.effect === "creative-rotate") {
    config.creativeEffect = {
      prev: { shadow: true, rotate: [0, 0, -90], translate: ["-100%", 0, 0] },
      next: { rotate: [0, 0, 90], translate: ["100%", 0, 0] },
    };
  }

  if (props.showNavigation && navNext && navPrev) {
    config.navigation = { nextEl: navNext, prevEl: navPrev };
  }

  if (props.showPagination && paginationEl) {
    config.pagination = {
      el: paginationEl,
      type: props.paginationType,
      clickable: true,
    };
  }

  if (props.autoplay) {
    config.autoplay = {
      delay: props.autoplayDelay,
      disableOnInteraction: false,
    };
  }

  if (props.effect === "coverflow") {
    config.centeredSlides = true;
    config.coverflowEffect = {
      rotate: 30,
      stretch: 0,
      depth: 100,
      modifier: 1,
      slideShadows: true,
    };
  }

  if (props.effect === "cards") {
    config.centeredSlides = true;
  }

  new Swiper(swiperContainer, config);
};
